import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { sdk } from '@/services/sdk';
import { useAuth } from '@/layouts/RootLayout';
import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Busy, Field, PageHeader, Panel } from '@/components/kit';
import { PickSelect, Pill, SwitchRow } from '@/components/inputs';
import { InlinePaymentNotice } from '@/components/PaymentNotice';
import {
  CONTACT_METHODS,
  MAX_CONNECTIONS,
  PLANS,
  TELLY,
  WRAITH_TERMS,
  contactPlaceholder,
  discountFor,
  money,
  newCode,
  paymentLabel,
  paymentMethodsFor,
  productPrice,
  productTerms,
  quote,
  termLabel,
  termsFor,
  upsellOffers,
} from '@/shared/pricing';
import { REFUND_POLICY } from '@/shared/policy';
import { committed, nowIso, rows } from '@/shared/data';
import { ensureMyCustomer, findMyCustomer } from '@/shared/account';
import VerifiedEmailCheck from '@/components/VerifyEmail';
import { DISCOUNT_PUBLIC_FIELDS, PRODUCT_FIELDS } from '@/shared/fields';
import { builtInState } from '@/shared/products';
import PaymentInstructions from '@/components/PaymentInstructions';
import Honeypot from '@/components/Honeypot';
import { useAntiSpam } from '@/shared/antispam';
import { track } from '@/shared/analytics';
import { checkReferralUsername, myUsernames, normUsername, qualifiesForReferral } from '@/shared/referrals';
import { clearStoredRef, readStoredRef } from '@/shared/referralLink';
import { useT } from '@/shared/i18n';
import { C } from '@/shared/locales/checkout/keys';

// Edge function that pings the owner on Discord/Telegram about a new order (no-op until its secrets are set).
const NEW_ORDER_ALERT = import.meta.env.VITE_NEW_ORDER_ALERT;

// Edge function that emails the buyer their confirmation and the owner a copy (no-op until email is set up).
const SEND_EMAIL = import.meta.env.VITE_SEND_EMAIL;

function emailConfirmation(orderId) {
  if (!SEND_EMAIL || !orderId) return;
  try {
    Promise.resolve(sdk.functions.invoke(SEND_EMAIL, { mode: 'order-confirmation', orderId })).catch(() => {});
  } catch {
    // email is best-effort and never blocks the order
  }
}

function alertNewOrder(orderId) {
  if (!NEW_ORDER_ALERT || !orderId) return;
  try {
    Promise.resolve(sdk.functions.invoke(NEW_ORDER_ALERT, { orderId })).catch(() => {});
  } catch {
    // alerts are best-effort and never block the order
  }
}

const { eq } = window.ApperSDK.CoreSDK;

export const route = { path: '/checkout', layout: 'owner', access: "profile === 'Customer_c'" };

const ALL_PLANS = ['Demon', 'Wraith', 'Banshee', 'Telly'];
const SOLD = ['Demon', 'Wraith', 'Telly'];

const maxFor = (plan) => {
  if (plan === 'Telly') return TELLY.maxQty;
  if (plan === 'Wraith' || plan === 'Product') return 1;
  return MAX_CONNECTIONS;
};

function normalize(plan, term, connections, product) {
  if (product) {
    const terms = productTerms(product);
    const t = terms.some((x) => x.id === term) ? term : (terms[0]?.id ?? '1m');
    return { plan: 'Product', term: t, connections: 1 };
  }
  const p = ALL_PLANS.includes(plan) ? plan : 'Demon';
  const terms = termsFor(p);
  const fallback = p === 'Demon' ? '1y' : terms[0].id;
  const t = terms.some((x) => x.id === term) ? term : fallback;
  const n = Math.min(maxFor(p), Math.max(1, Number(connections) || 1));
  return { plan: p, term: t, connections: n };
}

function YesNo({ value, onChange, name }) {
  const { t } = useT();
  return (
    <div className="flex gap-2" role="radiogroup" aria-label={name}>
      <Pill on={value === true} onClick={() => onChange(true)}>
        {t('Yes')}
      </Pill>
      <Pill on={value === false} onClick={() => onChange(false)}>
        {t('No')}
      </Pill>
    </div>
  );
}

function SummaryLine({ label, value, tone }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1.5 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className={tone}>{value}</span>
    </div>
  );
}

export default function Checkout() {
  const { t } = useT();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const productId = Number(params.get('product')) || null;
  const [sel, setSel] = useState(() =>
    normalize(params.get('plan'), params.get('term'), params.get('connections')),
  );
  const [product, setProduct] = useState(productId ? undefined : null);
  const [builtIns, setBuiltIns] = useState([]);

  useEffect(() => {
    let live = true;
    sdk
      .table('products_c')
      .select(PRODUCT_FIELDS)
      .fetch()
      .then(rows)
      .then((list) => {
        if (live) setBuiltIns(list.filter((p) => p.plan_key_c));
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, []);

  useEffect(() => {
    if (!productId) return undefined;
    let live = true;
    sdk
      .table('products_c')
      .select(PRODUCT_FIELDS)
      .get(productId)
      .then((r) => {
        if (!live) return;
        const found = r?.data && !r.data.plan_key_c && r.data.active_c && productTerms(r.data).length ? r.data : null;
        setProduct(found);
        if (found) setSel(normalize('Product', params.get('term'), 1, found));
      })
      .catch(() => {
        if (live) setProduct(null);
      });
    return () => {
      live = false;
    };
  }, [productId]);
  const [form, setForm] = useState(() => ({
    fullName: [user?.firstName, user?.lastName].filter(Boolean).join(' '),
    email: user?.emailAddress ?? '',
    contactMethod: 'Discord',
    handle: '',
    payment: 'Card',
    device: '',
    notes: '',
  }));
  const [adult, setAdult] = useState(null);
  const [ageOk, setAgeOk] = useState(false);
  const [trial, setTrial] = useState(null);
  const [tellyOn, setTellyOn] = useState(false);
  const [tellyQty, setTellyQty] = useState(1);
  const [promo, setPromo] = useState('');
  const [code, setCode] = useState(null);
  const [codeError, setCodeError] = useState('');
  // A friend's referral link (/r/<username>) or ?ref= fills this in.
  const [refInput, setRefInput] = useState(() => normUsername(params.get('ref')) || readStoredRef());
  const [refName, setRefName] = useState('');
  const [refError, setRefError] = useState('');
  const [refChecking, setRefChecking] = useState(false);
  const [checking, setChecking] = useState(false);
  const [refundOk, setRefundOk] = useState(false);
  const [optIn, setOptIn] = useState(false);
  const [savedVerified, setSavedVerified] = useState(false);
  const [justVerified, setJustVerified] = useState(false);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(null);
  const spam = useAntiSpam('checkout', { minMs: 3000, cooldownMs: 20000 });

  useEffect(() => {
    let live = true;
    findMyCustomer(user)
      .then((c) => live && setSavedVerified(!!c?.email_verified_c))
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [user?.emailAddress]);

  const accountEmail = (user?.emailAddress ?? '').trim().toLowerCase();
  const emailVerified =
    !!accountEmail &&
    form.email.trim().toLowerCase() === accountEmail &&
    (user?.isEmailConfirmed === true || savedVerified || justVerified);

  const isTelly = sel.plan === 'Telly';
  const isProduct = sel.plan === 'Product' && !!product;
  const onSale = (p) => builtInState(builtIns, p).onSale;
  const planTitle = isProduct ? product.Name : (PLANS[sel.plan]?.name ?? sel.plan);
  let base = null;
  if (isProduct) base = productPrice(product, sel.term);
  else if (onSale(sel.plan)) base = quote(sel.plan, sel.term, sel.connections);
  const addonQty = !isTelly && !isProduct && tellyOn ? tellyQty : 0;
  const tellyPrice = addonQty * TELLY.price;
  const price = (base ?? 0) + tellyPrice;
  const discount = code ? discountFor(code, sel.plan, base ?? 0) : 0;
  const total = Math.max(0, price - discount);
  const offers = useMemo(() => upsellOffers(sel), [sel]);
  const methods = paymentMethodsFor(sel.plan);
  const payment = methods.some((m) => m.value === form.payment) ? form.payment : methods[0].value;
  const epg = sel.plan === 'Wraith' && !!WRAITH_TERMS.find((t) => t.id === sel.term)?.epg;
  const sold = SOLD.filter(onSale);
  const planOptions = sel.plan === 'Banshee' ? ['Banshee', ...sold] : sold;
  const termOptions = isProduct
    ? productTerms(product).map((x) => ({ ...x, label: `${t(x.label)} · ${money(x.price)}` }))
    : termsFor(sel.plan);
  const max = maxFor(sel.plan);

  /** Title and detail of an upsell offer, in the visitor's language. */
  const offerTitle = (o) =>
    o.tKey ? t(o.tKey, { ...o.tVars, ...(o.tVars?.term ? { term: t(o.tVars.term) } : {}) }) : o.title;
  const offerDetail = (o) => (o.dKey ? `${t(o.dKey, o.dVars)}${o.dEpg ? ` · ${t(C.upEpg)}` : ''}` : o.detail);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const choose = (next) => setSel(normalize(next.plan, next.term, next.connections, isProduct ? product : null));

  async function applyCode() {
    const value = promo.trim().toUpperCase();
    if (!value) return;
    setChecking(true);
    setCodeError('');
    try {
      const r = await sdk
        .table('discount_codes_c')
        .select(DISCOUNT_PUBLIC_FIELDS)
        .where(eq('Name', value))
        .limit(1)
        .fetch();
      const found = r.success ? r.data?.[0] : null;
      let problem = '';
      if (!found || !found.active_c) problem = t(C.codeBad);
      else if (found.expires_at_c && new Date(found.expires_at_c).getTime() < Date.now()) problem = t(C.codeExpired);
      else if (Number(found.max_uses_c || 0) > 0 && Number(found.uses_c || 0) >= Number(found.max_uses_c)) {
        problem = t(C.codeUsed);
      } else if (!discountFor(found, sel.plan, base ?? 0)) {
        problem = t(C.codeNotFor, { plan: planTitle });
      }
      if (problem) {
        setCode(null);
        setCodeError(problem);
        return;
      }
      setCode(found);
      toast.success(t(C.codeApplied, { code: found.Name }));
    } catch (err) {
      setCodeError(err?.message || t(C.codeCant));
    } finally {
      setChecking(false);
    }
  }

  const refCounts = qualifiesForReferral({ plan_c: sel.plan, term_c: sel.term });

  /** Check the friend's username; returns it (normalised) when it's an active, paid line. */
  async function checkRef() {
    const value = refInput.trim();
    if (!value) return '';
    setRefChecking(true);
    setRefError('');
    try {
      const own = await myUsernames();
      const result = await checkReferralUsername(value, own);
      if (!result.ok) {
        setRefName('');
        setRefError(t(result.message));
        return '';
      }
      const name = normUsername(value);
      setRefName(name);
      toast.success(t(C.refBy, { name }));
      return name;
    } catch (err) {
      setRefError(err?.message || t(C.refCant));
      return '';
    } finally {
      setRefChecking(false);
    }
  }

  // Check a username that came from a referral link once, on arrival. Your own is dropped quietly.
  const refAutoChecked = useRef(false);
  useEffect(() => {
    if (refAutoChecked.current || !refInput.trim()) return;
    refAutoChecked.current = true;
    (async () => {
      try {
        const own = await myUsernames();
        if (own.map(normUsername).includes(normUsername(refInput))) {
          setRefInput('');
          clearStoredRef();
          return;
        }
      } catch {
        // checkRef reports any problem
      }
      checkRef();
    })();
  }, []);


  async function submit(e) {
    e.preventDefault();
    setFormError('');
    if (base == null) return setFormError(t(C.cNotSold));
    if (!form.fullName.trim() || !form.email.trim()) return setFormError(t(C.cNameEmail));
    if (!form.handle.trim()) return setFormError(t(C.cHandle, { method: form.contactMethod }));
    if (!isTelly && (adult === null || trial === null)) {
      return setFormError(t(C.cAnswer));
    }
    if (!isTelly && adult && !ageOk) return setFormError(t(C.cAge));
    if (!refundOk) return setFormError(t(C.cRefund));
    let referredBy = refName;
    if (!referredBy && refInput.trim()) {
      referredBy = await checkRef();
      if (!referredBy) return setFormError(t(C.cRefCheck));
    }
    const verdict = spam.check();
    if (!verdict.ok) {
      if (!verdict.silent) setFormError(verdict.message);
      return;
    }

    setSaving(true);
    try {
      const customer = await ensureMyCustomer(user, {
        name: form.fullName.trim(),
        email: form.email.trim(),
        contactMethod: form.contactMethod,
        handle: form.handle.trim(),
        optIn,
        verified: emailVerified,
      });
      if (!customer) return;

      const orderCode = newCode();
      const res = await sdk.table('orders_c').create({
        Name: orderCode,
        customer_c: customer.Id,
        full_name_c: form.fullName.trim(),
        email_c: form.email.trim(),
        email_verified_c: emailVerified,
        plan_c: sel.plan,
        ...(isProduct ? { product_c: product.Id } : {}),
        term_c: sel.term,
        connections_c: isProduct ? 1 : sel.connections,
        price_c: price,
        discount_code_c: code && discount > 0 ? code.Name : '',
        discount_c: discount,
        referred_by_c: referredBy || '',
        currency_c: 'USD',
        payment_method_c: payment,
        device_c: form.device.trim(),
        contact_method_c: form.contactMethod,
        contact_handle_c: form.handle.trim(),
        notes_c: form.notes.trim(),
        adult_content_c: !isTelly && adult === true,
        wants_trial_c: !isTelly && trial === true,
        status_c: 'Pending',
        telly_addon_c: addonQty > 0,
        telly_qty_c: addonQty,
        refund_accepted_at_c: nowIso(),
      });
      const order = committed(res);
      if (!order) return;
      spam.sent();
      alertNewOrder(order.Id);
      emailConfirmation(order.Id);
      if (referredBy) clearStoredRef();
      track('Order placed', { plan: sel.plan, payment });
      setDone({
        id: order.Id,
        code: orderCode,
        total,
        email: form.email.trim(),
        method: form.contactMethod,
        payment,
        trial: !isTelly && trial === true,
        isTelly,
        addonQty,
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSaving(false);
    }
  }

  if (done) {
    return (
      <div className="mx-auto max-w-xl">
        <div className="rounded-lg border border-border bg-card px-6 py-12 text-center text-card-foreground shadow-md">
          <span className="mx-auto flex size-16 items-center justify-center rounded-full bg-primary/10 text-primary ring-1 ring-primary/30">
            <ApperIcon name="Ghost" size={32} />
          </span>
          <h1 className="mt-6 font-heading text-4xl">{t(C.summoning)}</h1>
          <p className="mt-4 text-muted-foreground">{t(C.yourCode)}</p>
          <p className="mt-1 font-mono text-2xl tracking-[0.2em] text-primary">{done.code}</p>
          {done.trial ? (
            <p className="mx-auto mt-5 max-w-sm text-sm leading-6">
              {t(C.trialReach, { method: done.method, email: done.email })}
            </p>
          ) : (
            <>
              <PaymentInstructions
                method={done.payment}
                amount={done.total}
                reference={done.code}
                className="mx-auto mt-6 max-w-md"
                fallback={
                  <p className="mx-auto mt-5 max-w-sm text-sm leading-6">
                    {t(C.emailInstr, { email: done.email, method: t(paymentLabel(done.payment)) })}
                  </p>
                }
              />
              <p className="mx-auto mt-4 max-w-sm text-sm leading-6 text-muted-foreground">
                {done.isTelly
                  ? t(C.keyAppears)
                  : done.addonQty
                    ? t(C.lineActiveKeys, { name: TELLY.name, count: done.addonQty })
                    : t(C.lineActive)}
              </p>
            </>
          )}
          <div className="mt-8 flex flex-col justify-center gap-2 sm:flex-row">
            <Button asChild>
              <Link to={`/account/orders/${done.id}`}>
                <ApperIcon name="Package" size={16} />
                {t(C.viewOrder)}
              </Link>
            </Button>
            <Button variant="outline" asChild>
              <Link to="/account">{t(C.backAccount)}</Link>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (productId && product === undefined) {
    return (
      <div className="mx-auto max-w-6xl space-y-4" aria-busy="true">
        <div className="h-8 w-64 animate-pulse rounded-md bg-muted" />
        <div className="h-64 animate-pulse rounded-lg bg-muted" />
      </div>
    );
  }

  if (productId && !product) {
    return (
      <div className="mx-auto max-w-xl rounded-lg border border-border bg-card px-6 py-12 text-center">
        <ApperIcon name="PackageX" size={32} className="mx-auto text-muted-foreground" />
        <h1 className="mt-4 font-heading text-3xl">{t(C.notAvail)}</h1>
        <p className="mt-2 text-sm text-muted-foreground">{t(C.notAvailP)}</p>
        <Button className="mt-6" asChild>
          <Link to="/#plans">{t('See plans')}</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl">
      <Button variant="ghost" size="sm" asChild className="mb-4 -ms-2">
        <Link to="/account">
          <ApperIcon name="ArrowLeft" size={16} className="rtl:rotate-180" />
          {t(C.backAccount)}
        </Link>
      </Button>
      <PageHeader
        eyebrow={t(C.orderRequest)}
        title={
          <>
            <span className="text-primary">{planTitle}</span>
            {!isTelly && <span className="text-muted-foreground"> · {t(termLabel(sel.plan, sel.term))}</span>}
          </>
        }
        description={t(C.nothingCharged)}
      />

      <form onSubmit={submit} className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_22rem]">
        <Honeypot id="checkout-website" {...spam.trapProps} />
        <div className="space-y-6">
          <Panel title={t(C.yourPackage)} icon="Tv" bodyClassName="space-y-5 py-4">
            {isProduct ? (
              <div>
                <p className="font-medium">{product.Name}</p>
                {product.tagline_c && <p className="text-sm text-muted-foreground">{t(product.tagline_c)}</p>}
              </div>
            ) : (
              <Field label={t(C.plan)}>
                <div className="flex flex-wrap gap-2">
                  {planOptions.map((p) => (
                    <Pill key={p} on={sel.plan === p} onClick={() => choose({ ...sel, plan: p })}>
                      {PLANS[p]?.name ?? p}
                    </Pill>
                  ))}
                </div>
              </Field>
            )}
            {!isTelly && (
              <Field label={t('Term')}>
                <div className="flex flex-wrap gap-2">
                  {termOptions.map((x) => (
                    <Pill key={x.id} on={sel.term === x.id} onClick={() => choose({ ...sel, term: x.id })}>
                      {t(x.label)}
                    </Pill>
                  ))}
                </div>
              </Field>
            )}
            {max > 1 && (
              <Field label={isTelly ? t('Licences') : t(C.simConn)}>
                <div className="flex flex-wrap gap-2">
                  {Array.from({ length: isTelly ? max : MAX_CONNECTIONS }, (_, i) => i + 1).map((n) => (
                    <Pill key={n} on={sel.connections === n} onClick={() => choose({ ...sel, connections: n })}>
                      {n}
                    </Pill>
                  ))}
                </div>
              </Field>
            )}
            {epg && (
              <p className="flex items-center gap-2 text-sm text-highlight">
                <ApperIcon name="CalendarRange" size={16} />
                {t(C.inclEpg)}
              </p>
            )}
            {offers.length > 0 && (
              <div className="grid gap-2 sm:grid-cols-2">
                {offers.map((o) => (
                  <button
                    key={o.kind}
                    type="button"
                    onClick={() => choose(o.next)}
                    className="rounded-md border border-dashed border-highlight/50 bg-highlight/5 px-3 py-2.5 text-start transition-colors duration-(--transition-fast) hover:bg-highlight/10"
                  >
                    <span className="block text-sm font-medium text-highlight">{offerTitle(o)}</span>
                    <span className="block text-xs text-muted-foreground">{offerDetail(o)}</span>
                  </button>
                ))}
              </div>
            )}
            {!isTelly && !isProduct && (
              <div className="space-y-3">
                <SwitchRow
                  id="telly-addon"
                  label={t(C.addTelly, { name: TELLY.name, price: money(TELLY.price) })}
                  hint={t(C.tellyHint)}
                  checked={tellyOn}
                  onChange={setTellyOn}
                />
                {tellyOn && (
                  <Field label={t('Licences')} htmlFor="telly-qty">
                    <Input
                      id="telly-qty"
                      type="number"
                      min={1}
                      max={TELLY.maxQty}
                      value={tellyQty}
                      placeholder="1"
                      className="w-28"
                      onChange={(e) =>
                        setTellyQty(Math.min(TELLY.maxQty, Math.max(1, Number(e.target.value) || 1)))
                      }
                    />
                  </Field>
                )}
              </div>
            )}
          </Panel>

          <Panel title={t(C.reach)} icon="Radio" bodyClassName="grid gap-4 py-4 sm:grid-cols-2">
            <Field label={t(C.fullName)} htmlFor="full-name">
              <Input id="full-name" value={form.fullName} onChange={set('fullName')} placeholder="Maya Okafor" />
            </Field>
            <Field label={t(C.emailPay)} htmlFor="email">
              <Input id="email" type="email" value={form.email} onChange={set('email')} placeholder="you@example.com" />
            </Field>
            <VerifiedEmailCheck
              className="sm:col-span-2"
              user={user}
              email={form.email}
              verified={emailVerified}
              onVerified={() => setJustVerified(true)}
            />
            <Field label={t(C.oneMore)} htmlFor="contact-method">
              <PickSelect
                id="contact-method"
                value={form.contactMethod}
                onChange={(v) => setForm((f) => ({ ...f, contactMethod: v }))}
                values={CONTACT_METHODS.map((c) => c.id)}
              />
            </Field>
            <Field label={t(C.handleLbl, { method: form.contactMethod })} htmlFor="handle">
              <Input
                id="handle"
                value={form.handle}
                onChange={set('handle')}
                placeholder={contactPlaceholder(form.contactMethod)}
              />
            </Field>
            <Field label={t(C.payMethod)} htmlFor="payment">
              <PickSelect
                id="payment"
                value={payment}
                onChange={(v) => setForm((f) => ({ ...f, payment: v }))}
                values={methods.map((m) => m.value)}
                labels={Object.fromEntries(methods.map((m) => [m.value, t(m.label)]))}
              />
            </Field>
            <Field label={isTelly ? t(C.devicesOpt) : t(C.mainDevice)} htmlFor="device">
              <Input id="device" value={form.device} onChange={set('device')} placeholder={t(C.devicePh)} />
            </Field>
          </Panel>

          {!isTelly && (
            <Panel title={t(C.twoQ)} icon="MessageCircleQuestion" bodyClassName="space-y-5 py-4">
              <Field label={t(C.adultQ)}>
                <YesNo name={t(C.adultQ)} value={adult} onChange={setAdult} />
                {adult === true && (
                  <label className="mt-3 flex items-center gap-2 text-sm" htmlFor="age-ok">
                    <Checkbox id="age-ok" checked={ageOk} onCheckedChange={(v) => setAgeOk(v === true)} />
                    {t(C.confirm18)}
                  </label>
                )}
              </Field>
              <Field label={t(C.trialQ)}>
                <YesNo name={t(C.trialQ)} value={trial} onChange={setTrial} />
                {trial === true && (
                  <p className="mt-2 text-xs text-muted-foreground">
                    {t(C.trialNote)}
                  </p>
                )}
              </Field>
            </Panel>
          )}

          <Panel title={t(C.anything)} icon="NotebookPen" bodyClassName="space-y-4 py-4">
            <Field label={t(C.notes)} htmlFor="notes">
              <Textarea
                id="notes"
                rows={3}
                value={form.notes}
                onChange={set('notes')}
                placeholder={t(C.notesPh)}
              />
            </Field>
            <Field label={t(C.discount)} htmlFor="promo">
              {code ? (
                <div className="flex items-center justify-between gap-3 rounded-md border border-success-border bg-success-muted px-3 py-2 text-sm text-success">
                  <span className="flex items-center gap-2">
                    <ApperIcon name="TicketPercent" size={16} />
                    {code.Name} · {discount > 0 ? `−${money(discount)}` : t(C.notValid)}
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setCode(null);
                      setPromo('');
                    }}
                  >
                    {t(C.remove)}
                  </Button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Input
                    id="promo"
                    value={promo}
                    onChange={(e) => setPromo(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        applyCode();
                      }
                    }}
                    placeholder="e.g. BOO10"
                    className="uppercase"
                  />
                  <Button type="button" variant="outline" onClick={applyCode} disabled={!promo.trim() || checking}>
                    <Busy busy={checking} label={t(C.apply)} busyLabel={t(C.checkingL)} />
                  </Button>
                </div>
              )}
              {codeError && <p className="mt-1.5 text-xs text-destructive">{codeError}</p>}
            </Field>
            <Field label={t(C.refQ)} htmlFor="ref-user">
              {refName ? (
                <div className="flex items-center justify-between gap-3 rounded-md border border-success-border bg-success-muted px-3 py-2 text-sm text-success">
                  <span className="flex min-w-0 items-center gap-2">
                    <ApperIcon name="Users" size={16} className="shrink-0" />
                    <span className="truncate">
                      {t(C.refBy, { name: refName })}
                    </span>
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setRefName('');
                      setRefInput('');
                      setRefError('');
                      clearStoredRef();
                    }}
                  >
                    {t(C.remove)}
                  </Button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Input
                    id="ref-user"
                    value={refInput}
                    onChange={(e) => {
                      setRefInput(e.target.value);
                      setRefError('');
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        checkRef();
                      }
                    }}
                    placeholder={t(C.refPh)}
                    autoComplete="off"
                    spellCheck={false}
                  />
                  <Button type="button" variant="outline" onClick={checkRef} disabled={!refInput.trim() || refChecking}>
                    <Busy busy={refChecking} label={t(C.check)} busyLabel={t(C.checkingL)} />
                  </Button>
                </div>
              )}
              {refError && <p className="mt-1.5 text-xs text-destructive">{refError}</p>}
              <p className={`mt-1.5 text-xs ${refName && !refCounts ? 'text-warning' : 'text-muted-foreground'}`}>
                {refName && !refCounts ? t(C.refShort) : t(C.refHint)}
              </p>
            </Field>
          </Panel>
        </div>

        <aside className="lg:sticky lg:top-20 lg:self-start">
          <div className="rounded-lg border border-border bg-card p-5 text-card-foreground shadow-md">
            <p className="text-[11px] uppercase tracking-[0.22em] text-primary">{t(C.summary)}</p>
            <div className="mt-3 divide-y divide-border">
              <SummaryLine
                label={
                  isTelly
                    ? `${TELLY.name} × ${sel.connections}`
                    : isProduct
                      ? `${product.Name} · ${t(termLabel('Product', sel.term))}`
                      : `${planTitle} · ${t(C.connN, { count: sel.connections })}`
                }
                value={base == null ? t(C.notSold) : money(base)}
                tone={base == null ? 'text-destructive' : undefined}
              />
              {addonQty > 0 && <SummaryLine label={`${TELLY.name} × ${addonQty}`} value={money(tellyPrice)} />}
              {discount > 0 && <SummaryLine label={t(C.codeLine, { code: code.Name })} value={`−${money(discount)}`} tone="text-success" />}
              <div className="flex items-baseline justify-between pt-3">
                <span className="text-sm">{t(C.total)}</span>
                <span className="font-heading text-4xl text-primary">{money(total)}</span>
              </div>
            </div>
            {!isTelly && (
              <Badge variant="outline" className="mt-3 border-highlight/40 bg-highlight/10 text-highlight">
                {t(termLabel(sel.plan, sel.term))}
              </Badge>
            )}

            <div className="mt-5 space-y-3 border-t border-border pt-4 text-sm">
              <label className="flex items-start gap-2.5" htmlFor="refund-ok">
                <Checkbox
                  id="refund-ok"
                  className="mt-0.5"
                  checked={refundOk}
                  onCheckedChange={(v) => setRefundOk(v === true)}
                />
                <span>
                  {t(REFUND_POLICY.accept)}
                  <span className="mt-1 block text-xs text-muted-foreground">
                    {t(C.seeThe)} <Link to="/refund-policy" className="underline underline-offset-2">{t(C.refundPolicy)}</Link>,{' '}
                    <Link to="/terms" className="underline underline-offset-2">{t(C.termsL)}</Link> {t(C.and)}{' '}
                    <Link to="/privacy" className="underline underline-offset-2">{t(C.privacyL)}</Link>.
                  </span>
                </span>
              </label>
              <label className="flex items-start gap-2.5" htmlFor="opt-in">
                <Checkbox id="opt-in" className="mt-0.5" checked={optIn} onCheckedChange={(v) => setOptIn(v === true)} />
                <span>
                  {t(C.offers)}
                  <span className="block text-xs text-muted-foreground">{t(C.optional)}</span>
                </span>
              </label>
            </div>

            {formError && (
              <p className="mt-4 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive" role="alert">
                {formError}
              </p>
            )}

            <InlinePaymentNotice className="mt-5" />

            <Button type="submit" className="mt-5 w-full" size="lg" disabled={saving || base == null}>
              <Busy
                busy={saving}
                busyLabel={t('Sending…')}
                label={
                  <>
                    <ApperIcon name="Send" size={16} />
                    {t(C.sendOrder)}
                  </>
                }
              />
            </Button>
            <p className="mt-3 text-center text-xs text-muted-foreground">
              {t(C.paidBy, { method: t(paymentLabel(payment)) })}
            </p>
          </div>
        </aside>
      </form>
    </div>
  );
}
